import React, {useEffect, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {Field, FieldsManager} from '../../../data/services/fields'
import LocalStorage from '../../../util/localStorage'
import Resources from '../../../data/services/resources'
import {getResource} from '../../../data/actions/resource'
import {
    ACCOUNT_TYPE_OTHER_CURRENT_ASSETS,
    DEFAULT_METADATA_SELECT_SEARCH_QUERY
} from '../../../util/util-constants'
import PayDateFilter from '../carrier-pay/carrier-pay-date-filter'
import PopOver from '../../../common/components/popover'
import CalendarIcon from '@heroicons/react/20/solid/CalendarIcon'
import moment from 'moment-timezone'
import {toFrontDate} from '../../../common/util/util-dates'
import {excludeFields} from '../../../common/util/util-fields'
import TableFilters from '../../../common/components/resource-table/table-components/table-filters'
import ActiveFilters from '../../../common/components/resource-table/table-components/active-filters'
import AdvancesTrend from './advances-trend'

const selects = {
    Employee: {'': 'All', '0': 'Hide employees', '1': 'Show employees'},
    Agent: {'': 'All', '0': 'Hide agents', '1': 'Show agents'},
    Driver: {'': 'All', '0': 'Hide drivers', '1': 'Show drivers'},
    AccountIDs: {
        api: 'api/' + Resources.AccountsQuick,
        query: {AccountTypeID: ACCOUNT_TYPE_OTHER_CURRENT_ASSETS, ...DEFAULT_METADATA_SELECT_SEARCH_QUERY()},
        searchMap: (item) => ({label: item.AccountNumber + ' ' + item.AccountName, value: item.AccountID})
    },
}

export default function AdvancesTrendTab({translate}) {
    const dispatch = useDispatch()
    const resource = useSelector((state) => state.resource)

    const isLoading = resource.isLoading
    const trendData = resource?.data ?? null

    const [query, setQuery] = useState(getQueryFields())

    function fetchData() {
        dispatch(getResource({
            user: LocalStorage.get('user'),
            resource: Resources.AccountingAccountAdvances,
            query: Object.assign({Trend: 1}, processQuery())
        }))
    }

    function handleQueryChange(name, value) {
        let queryClone = Object.assign({}, query)

        if (!name && !value) {
            queryClone = Object.values(queryClone).reduce((memo, it) => {
                it.value = ''
                memo[it.name] = it
                return memo
            }, {})
        } else {
            queryClone = FieldsManager.updateField(queryClone, name, value)
        }

        setQuery(queryClone)
    }

    function getQueryFields() {
        return {
            StartDate: new Field('StartDate', moment().subtract(1, 'week').startOf('week').format('YYYY-MM-DD HH:mm:ss'), [], false, 'date', {}, {}),
            EndDate: new Field('EndDate', moment().subtract(1, 'week').endOf('week').format('YYYY-MM-DD HH:mm:ss'), [], false, 'date', {}, {}),
            Employee: new Field('Employee', '', [''], false, 'select', {labelType: 'float'}, {isClearable: true}),
            Agent: new Field('Agent', '', [''], false, 'select', {labelType: 'float'}, {isClearable: true}),
            Driver: new Field('Driver', '', [''], false, 'select', {labelType: 'float'}, {isClearable: true}),
            AccountIDs: new Field('AccountIDs', '', [''], false, 'select-search', {}, {multi: true, isClearable: true}),
            IsAdvanceAccount: new Field('IsAdvanceAccount', '', [''], false, 'checkbox'),
        }
    }

    function processQuery() {
        return Object.values(query).reduce((memo, it) => {
            if (it.name === 'IsAdvanceAccount' && !!it.value) {
                memo['IsAdvanceAccount'] = 1
            } else if (it.name === 'Employee' && it.value === '1') {
                memo['IsEmployee'] = 1
            } else if (it.name === 'Employee' && it.value === '0') {
                memo['NotEmployees'] = 1
            } else if (it.name === 'Agent' && it.value === '1') {
                memo['IsAgent'] = 1
            } else if (it.name === 'Agent' && it.value === '0') {
                memo['NotAgents'] = 1
            } else if (it.name === 'Driver' && it.value === '1') {
                memo['IsDriver'] = 1
            } else if (it.name === 'Driver' && it.value === '0') {
                memo['NotDrivers'] = 1
            } else if (it.name === 'AccountIDs') {
                memo[it.name] = it?.value?.length ? it.value.map(it => it.value) : ''
            } else {
                memo[it.name] = it.value
            }
            return memo
        }, {})
    }

    const getDateFilterLabel = () => {
        const {StartDate, EndDate} = query
        if (!StartDate.value || !EndDate.value) return translate('text.select_date_range') || 'Select dates'
        return `${toFrontDate(StartDate.value)} – ${toFrontDate(EndDate.value)}`
    }

    const getFilterSummary = () => {
        const skip = ['StartDate', 'EndDate']
        return Object.values(query)
            .filter(f => !skip.includes(f.name) && f.value && !(Array.isArray(f.value) && f.value.length === 0))
            .map(f => {
                if (Array.isArray(f.value)) return f.value.map(v => v?.label ?? v).join(', ')
                return f.value?.label ?? f.value
            })
            .filter(Boolean)
            .join(' · ')
    }

    const getFilterHeaderLabel = () => {
        const dateLabel = getDateFilterLabel()
        const summary = getFilterSummary()
        return summary ? `${dateLabel} · ${summary}` : dateLabel
    }

    const makeCustomHtml = () => (
        <>
            <PopOver
                className="relative"
                btnClass="text-opacity-90 form-control p-0 w-auto text-tm-gray-700 flex items-center py-2 px-3"
                BtnIcon={CalendarIcon}
                btnIconClass="h-4 w-4 mr-1"
                chevronIcon={true}
                chevronIconClass="ml-2 h-4 w-4 text-tm-gray-700 transition ease-in-out duration-150"
                btnLabel={getDateFilterLabel()}
                widthClass="max-w-2xl"
                positionClass="absolute translate-x-0 left-0"
            >
                <div className="bg-popup border border-tm-gray-300 rounded-lg">
                    <PayDateFilter
                        queryFields={query}
                        translate={translate}
                        updateQueryFields={(qf) => setQuery(qf)}
                        onQueryChange={handleQueryChange}
                        isDateTypeClearable={false}
                        hasDateType={false}
                    />
                </div>
            </PopOver>

            {[
                {name: 'Employee', label: 'Employee', opts: {'': 'All', '0': 'Hide employees', '1': 'Show employees'}},
                {name: 'Agent', label: 'Agent', opts: {'': 'All', '0': 'Hide agents', '1': 'Show agents'}},
                {name: 'Driver', label: 'Driver', opts: {'': 'All', '0': 'Hide drivers', '1': 'Show drivers'}},
            ].map(({name, label, opts}) => (
                <div key={name} className="col-span-2">
                    <label className="text-xs text-tm-gray-600 mb-1 block">{label}</label>
                    <select
                        className="form-control w-full"
                        value={query[name]?.value ?? ''}
                        onChange={(e) => handleQueryChange(name, e.target.value)}
                    >
                        {Object.entries(opts).map(([val, lbl]) => (
                            <option key={val} value={val}>{lbl}</option>
                        ))}
                    </select>
                </div>
            ))}
        </>
    )

    const makeFilterPanel = () => (
        <div>
            <ActiveFilters
                addClass="px-4 pt-2"
                filterFields={query}
                onLabelClick={handleQueryChange}
                persistFilters={['StartDate', 'EndDate']}
                onClearFiltersClick={handleQueryChange}
                translate={translate}
            />
            <TableFilters
                forceDialog
                hideLimit
                filterFields={excludeFields(query, ['StartDate', 'EndDate', 'Employee', 'Agent', 'Driver'])}
                handleInputChange={handleQueryChange}
                translate={translate}
                onRefreshTable={fetchData}
                selects={selects}
                isLoading={isLoading}
                customHtml={makeCustomHtml()}
            />
        </div>
    )

    useEffect(() => {
        fetchData()
    }, [query])

    return (
        <div>
            <div className="md:bg-inverse md:border border-tm-gray-300 md:shadow-card">
                <div className="px-4 pt-2">
                    <span className="text-xs text-tm-gray-400">{getFilterHeaderLabel()}</span>
                </div>
                {makeFilterPanel()}
            </div>

            <AdvancesTrend
                data={trendData}
                isLoading={isLoading}
                translate={translate}
            />
        </div>
    )
}
